import React, { useState, useRef, useEffect } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";

/** Vasa chat — searches notes (optionally scoped to a tour or tag-set) and answers via GPT-5.2. */
export default function VasaChat({ tourId, tags, compact = false, onCitationClick }) {
  const [messages, setMessages] = useState([
    { role: "vasa", text: "I'm Vasa. Ask me anything about your notes — I'll quote chapter and verse.", citations: [] },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const q = input.trim();
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await auleloApi.post("/vasa/chat", { query: q, tour_id: tourId, tags, limit: 6 });
      setMessages((m) => [...m, { role: "vasa", text: data.answer, citations: data.citations || [], matched: data.matched }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vasa is offline");
      setMessages((m) => [...m, { role: "vasa", text: "I hit a snag. Try again in a moment.", citations: [] }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex flex-col border border-emerald-700/30 bg-black ${compact ? "h-[420px]" : "h-[560px]"}`} data-testid="vasa-chat">
      <div className="border-b border-emerald-700/30 px-4 py-3 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
        <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-emerald-300">vasa · notes companion</div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3" data-testid="vasa-chat-thread">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] px-3 py-2 border ${m.role === "user" ? "border-emerald-600/40 bg-emerald-500/10 text-emerald-100" : "border-zinc-800 bg-zinc-950 text-zinc-200"}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500 mb-1">{m.role === "user" ? "you" : "vasa"}</div>
              <div className="font-mono text-[13px] leading-relaxed whitespace-pre-wrap">{m.text}</div>
              {m.citations && m.citations.length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-800 flex flex-wrap gap-1">
                  {m.citations.map((c, idx) => (
                    <button key={c.id} onClick={() => onCitationClick?.(c)} data-testid={`vasa-citation-${idx}`}
                      className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-700/30 text-emerald-300 hover:bg-emerald-500/10">
                      <BookOpen className="w-3 h-3 inline mr-1" /> note {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-emerald-700/30 p-3 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={tourId ? "Ask about this visit…" : "Ask Vasa about your notes…"}
          data-testid="vasa-chat-input"
          className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-10 font-mono"
        />
        <Button type="submit" disabled={busy || !input.trim()} data-testid="vasa-chat-send"
          className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[10px] h-10 px-4">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </form>
    </div>
  );
}
